import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { AnimatePresence, motion, type PanInfo } from "motion/react";
import { type SyntheticEvent, useEffect, useState } from "react";
import {
  FiBell,
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiGrid,
  FiHome,
  FiList,
  FiMonitor,
  FiMusic,
  FiRefreshCw,
  FiSearch,
  FiSettings,
  FiShieldOff,
  FiWatch,
} from "react-icons/fi";
import { MenuItem } from "@/app/layouts/MenuItem";
import noImage from "@/assets/img/no-image.svg";
import { boardPublicPath } from "@/config/browserBoard";
import type { ThreadSummary } from "@/entities/thread";
import {
  getImageUrl,
  getThreadTitle,
  isThreadExpired,
} from "@/entities/thread";
import { AnnouncementNavLabel } from "@/features/announce/components/AnnouncementNavLabel";
import { useAnnouncementStatus } from "@/features/announce/hooks/useAnnouncementStatus";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { HistoryModeToggle } from "@/features/history/components/HistoryModeToggle";
import { historyThreadKey } from "@/features/history/historyIdentity";
import {
  type HistoryMode,
  historyModeLabel,
} from "@/features/history/historyMode";
import {
  selectHistoryItemsForScope,
  useHistorySummaryScope,
} from "@/features/history/hooks/historySummaryScope";
import { fetchHistoryThreadSummaries } from "@/features/history/hooks/useHistoryThreads";
import { useHistoryStore } from "@/features/history/stores";
import {
  selectActivePostedItems,
  usePostedHistoryStore,
} from "@/features/history/stores/postedHistoryStore";
import { useSettingsStore } from "@/features/settings/hooks";
import { buildForcePcUrl } from "@/shared/lib/forcePc";
import { createEscapeKeyHandler } from "@/shared/lib/keyboardAccessibility";
import {
  BOARD_RULES_HREF,
  MIX_CATALOG_HREF,
  useFromMixCatalog,
} from "@/shared/lib/mixReturn";
import { TextLink } from "@/shared/ui/navigation/TextLink";
import { DialogCloseButton } from "@/shared/ui/overlay/DialogCloseButton";
import { getDialogAccessibilityProps } from "@/shared/ui/overlay/dialogAccessibility";
import { useDialogFocusTrap } from "@/shared/ui/overlay/dialogFocus";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const SideMenu: React.FunctionComponent<Props> = ({
  isOpen,
  onClose,
}: Props) => {
  const getUnreadCount = useHistoryStore((state) => state.getUnreadCount);
  const viewed = useHistoryStore((state) => state.viewed);
  const posted = usePostedHistoryStore((state) => state.posted);
  const [historyMode, setHistoryMode] = useState<HistoryMode>("viewed");
  const fromMixCatalog = useFromMixCatalog();
  const historyScope = useHistorySummaryScope();
  const historyItems = selectHistoryItemsForScope(
    historyMode === "posted" ? selectActivePostedItems(posted) : viewed,
    historyScope,
  )
    .slice(0, 20)
    .map(({ boardSlug, threadId }) => ({ boardSlug, threadId }));
  const jukeboxEnabled = useSettingsStore((s) => s.jukeboxEnabled);
  const { hasUnreadAnnouncements } = useAnnouncementStatus();
  const dialogRef = useDialogFocusTrap<HTMLElement>(isOpen);
  const forcePcUrl =
    typeof window === "undefined" ? "/?pc=1" : buildForcePcUrl(window.location);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = createEscapeKeyHandler(onClose);

    window.addEventListener("keydown", handleKeyDown);
    return (): void => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const {
    data: historyThreads = [],
    isError: isHistoryError,
    isFetching: isHistoryFetching,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: [
      "history-threads",
      historyScope.slugs,
      historyScope.tagBoard,
      historyMode,
      historyItems,
    ],
    queryFn: async (): Promise<ThreadSummary[]> => {
      if (historyItems.length === 0) return [];
      return fetchHistoryThreadSummaries(historyScope, historyItems);
    },
    enabled: isOpen && historyItems.length > 0,
    staleTime: 60_000,
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            ref={dialogRef}
            tabIndex={-1}
            {...getDialogAccessibilityProps("side-menu-title", true)}
            className="fixed top-0 left-0 bottom-0 w-72 pl-[env(safe-area-inset-left)] bg-card z-50 flex flex-col overflow-y-auto overscroll-contain"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={{ left: 0.8, right: 0 }}
            onDragEnd={(_e: unknown, info: PanInfo): void => {
              if (info.offset.x < -100 || info.velocity.x < -500) {
                onClose();
              }
            }}
          >
            {/* ヘッダー */}
            <header className="flex items-center justify-between px-4 pt-[env(safe-area-inset-top)] border-b border-border bg-card sticky top-0 w-full flex-none basis-14">
              <h1
                id="side-menu-title"
                className="text-lg font-bold text-foreground"
              >
                メニュー
              </h1>
              <DialogCloseButton
                onClose={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted"
              />
            </header>

            {/* メニュー項目 */}
            <nav className="p-2 border-b border-border">
              {fromMixCatalog ? (
                <MenuItem
                  icon={<FiGrid className="w-5 h-5" />}
                  href={MIX_CATALOG_HREF}
                  onClick={onClose}
                  isInternal
                >
                  カタログ
                </MenuItem>
              ) : (
                <MenuItem
                  icon={<FiGrid className="w-5 h-5" />}
                  to="/"
                  onClick={onClose}
                  isInternal
                >
                  カタログ
                </MenuItem>
              )}
              <MenuItem
                icon={<FiBell className="w-5 h-5" aria-hidden="true" />}
                to="/announcements"
                onClick={onClose}
                isInternal
                className="min-h-11"
              >
                <AnnouncementNavLabel isNew={hasUnreadAnnouncements} />
              </MenuItem>
              <MenuItem
                icon={<FiSettings className="w-5 h-5" />}
                to="/settings"
                onClick={onClose}
                isInternal
              >
                設定
              </MenuItem>
              <MenuItem
                icon={<FiWatch className="w-5 h-5" />}
                to="/archive"
                onClick={onClose}
              >
                過去ログ
              </MenuItem>
              <MenuItem
                icon={<FiHome className="w-5 h-5" />}
                href="/"
                isInternal
                onClick={onClose}
              >
                ホーム
              </MenuItem>

              <details className="group [&>:not(summary)]:ml-2 [&>:not(summary)]:w-[calc(100%-0.5rem)]">
                <summary className="grid min-h-11 cursor-pointer list-none grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-lg px-3 py-2 text-foreground transition-colors hover:bg-muted [&::-webkit-details-marker]:hidden">
                  <FiChevronDown
                    className="h-5 w-5 transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                  すべて表示
                </summary>
                <MenuItem
                  icon={<FiList className="w-5 h-5" />}
                  to="/board"
                  onClick={onClose}
                  isInternal
                >
                  リスト
                </MenuItem>
                <MenuItem
                  icon={<FiSearch className="w-5 h-5" />}
                  to="/search"
                  onClick={onClose}
                  isInternal
                >
                  投稿検索
                </MenuItem>
                {jukeboxEnabled && (
                  <MenuItem
                    icon={<FiMusic className="w-5 h-5" />}
                    to="/jukebox"
                    onClick={onClose}
                    isInternal
                  >
                    ジュークボックス
                  </MenuItem>
                )}
                <MenuItem
                  icon={<FiShieldOff className="w-5 h-5" />}
                  href={BOARD_RULES_HREF}
                  isInternal
                >
                  利用規約
                </MenuItem>
                <MenuItem
                  icon={<FiClock className="w-5 h-5" />}
                  href={boardPublicPath("/history/viewed")}
                  isInternal
                  onClick={onClose}
                >
                  履歴
                </MenuItem>
                <MenuItem
                  icon={<FiMonitor className="w-5 h-5" />}
                  href={forcePcUrl}
                  isInternal
                >
                  PC版に切替
                </MenuItem>
              </details>
            </nav>

            {/* 履歴 */}
            <section aria-label="履歴" className="flex-1">
              <header className="flex items-center justify-between px-4 py-2 text-sm font-medium text-muted-foreground bg-card sticky top-14 w-full">
                <TextLink
                  to="/history/$mode"
                  params={{ mode: "viewed" }}
                  onClick={onClose}
                  variant="muted"
                  aria-label="履歴ページを開く"
                  className="inline-flex items-center gap-0.5"
                >
                  履歴
                  <FiChevronRight className="w-4 h-4" aria-hidden="true" />
                </TextLink>
                {historyItems.length > 0 && (
                  <button
                    type="button"
                    onClick={(): void => void refetchHistory()}
                    disabled={isHistoryFetching}
                    aria-label={`${historyModeLabel(historyMode)}を再取得`}
                    className="rounded p-1 hover:bg-muted disabled:opacity-50"
                  >
                    <FiRefreshCw
                      className={isHistoryFetching ? "animate-spin" : ""}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </header>
              <HistoryModeToggle
                mode={historyMode}
                onChange={setHistoryMode}
                className="side-menu-history-mode-toggle"
              />
              {historyItems.length === 0 ? (
                <p className="px-4 py-8 text-center text-muted-foreground text-sm">
                  {historyModeLabel(historyMode)}がありません
                </p>
              ) : isHistoryError ? (
                <p className="px-4 py-8 text-center text-muted-foreground text-sm">
                  {historyModeLabel(historyMode)}を取得できませんでした
                </p>
              ) : historyThreads.length === 0 && isHistoryFetching ? (
                <p className="px-4 py-8 text-center text-muted-foreground text-sm">
                  {historyModeLabel(historyMode)}を読み込み中...
                </p>
              ) : (
                <ol
                  aria-label={historyModeLabel(historyMode)}
                  className="m-0 list-none space-y-1 px-2 pb-4"
                >
                  {historyItems.map((item) => {
                    const thread = historyThreads.find(
                      (candidate) =>
                        candidate.id === item.threadId &&
                        (candidate.b ?? item.boardSlug) === item.boardSlug,
                    );
                    if (!thread) return null;
                    const unreadCount = getUnreadCount(
                      item.boardSlug,
                      thread.id,
                      thread.replyCount,
                    );
                    return (
                      <li key={historyThreadKey(item)}>
                        <CatalogThreadLink
                          thread={thread}
                          onClick={onClose}
                          className={clsx(
                            "grid grid-cols-[3rem_minmax(0,1fr)] grid-rows-2 items-center gap-x-3 rounded-lg p-2 transition-colors hover:bg-muted",
                            isThreadExpired(thread) && "catalog-item-expired",
                          )}
                        >
                          <img
                            src={getImageUrl(thread.opPost.attachment, false)}
                            alt=""
                            className="row-span-2 h-12 w-12 rounded bg-muted object-cover"
                            onError={(
                              event: SyntheticEvent<HTMLImageElement>,
                            ): void => {
                              event.currentTarget.src = noImage;
                            }}
                          />
                          <b className="block min-w-0 truncate text-sm font-normal text-foreground">
                            {getThreadTitle(thread)}
                          </b>
                          <small className="block text-xs text-muted-foreground">
                            {thread.replyCount}レス
                            {unreadCount && (
                              <strong className="mx-1 font-normal text-primary">
                                未読{unreadCount}レス
                              </strong>
                            )}
                          </small>
                        </CatalogThreadLink>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
